import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { HlsVideo } from "@/components/media/HlsVideo";
import { RATIO_CLASS } from "./blockConfig";

const Media = ({ item, className, controls }) =>
  item.kind === "video" ? (
    <HlsVideo src={mediaUrl(item.id)} className={className} controls={controls} autoPlay={controls} muted={!controls} playsInline preload="metadata" />
  ) : (
    <img src={mediaUrl(item.id)} alt="" loading="lazy" className={className} />
  );

export const Lightbox = ({ items, index, onClose, setIndex }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items.length, onClose, setIndex]);
  const nav = "absolute top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-[#E62314]";
  return (
    <div data-testid="gallery-lightbox" data-lenis-prevent className="fixed inset-0 z-[80] flex items-center justify-center bg-black/95 p-4" onClick={onClose}>
      <div className="max-h-[85vh] max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
        <Media key={items[index].id} item={items[index]} controls className="max-h-[85vh] max-w-[92vw] object-contain" />
      </div>
      <button onClick={onClose} aria-label="Close" data-testid="gallery-lightbox-close" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-[#E62314]"><X className="h-5 w-5" /></button>
      {items.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setIndex((i) => (i - 1 + items.length) % items.length); }} aria-label="Previous" className={`${nav} left-4`} data-testid="gallery-lightbox-prev"><ChevronLeft /></button>
          <button onClick={(e) => { e.stopPropagation(); setIndex((i) => (i + 1) % items.length); }} aria-label="Next" className={`${nav} right-4`} data-testid="gallery-lightbox-next"><ChevronRight /></button>
          <p className="absolute bottom-5 left-1/2 -translate-x-1/2 font-mono-label text-xs text-zinc-300">{index + 1} / {items.length}</p>
        </>
      )}
    </div>
  );
};

export const Gallery = ({ block, editing, onRemoveItem }) => {
  const [open, setOpen] = useState(-1);
  const items = Array.isArray(block.items) ? block.items : [];
  const cols = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-2 md:grid-cols-3", 4: "grid-cols-2 md:grid-cols-4", 5: "grid-cols-2 md:grid-cols-5" }[block.columns] || "grid-cols-2 md:grid-cols-3";
  const ratio = RATIO_CLASS[block.ratio] || "aspect-square";
  if (!items.length) return editing ? <p className="py-10 text-center text-sm text-[#57534E]">Empty gallery: open this block's settings to add photos and videos.</p> : null;
  return (
    <>
      <div className={`grid ${cols}`} style={{ gap: `${block.gap ?? 12}px` }} data-testid="gallery-grid">
        {items.map((it, i) => (
          <div key={it.id} className="group relative overflow-hidden bg-[#ECEAE6]">
            <button type="button" onClick={() => setOpen(i)} className={`block w-full ${ratio}`} data-testid={`gallery-item-${i}`} aria-label="Open">
              <Media item={it} className={`h-full w-full ${ratio ? "object-cover" : "h-auto"} transition-transform duration-700 group-hover:scale-105`} />
            </button>
            {it.kind === "video" && <span className="pointer-events-none absolute left-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-bold text-white">Video</span>}
            {editing && <button onClick={() => onRemoveItem(i)} aria-label="Remove" className="absolute right-2 top-2 rounded-full bg-black/80 p-1.5 text-white hover:bg-[#E62314]" data-testid={`gallery-remove-${i}`}><X className="h-3.5 w-3.5" /></button>}
          </div>
        ))}
      </div>
      {open >= 0 && <Lightbox items={items} index={open} setIndex={setOpen} onClose={() => setOpen(-1)} />}
    </>
  );
};
